/**
 * API-слой кабинета Рублокса (внутренняя часть сайта после входа).
 *
 * Повторяет ApiClient.kt из Android-приложения — те же эндпоинты, те же
 * параметры. База: minecraftia-school.ru, два микросервиса:
 *   api-user   (/api-user/api/v1/...)  — профиль, экономика, рейтинг, друзья, сообщения
 *   api-storys (/api-storys/kubikon3d/...) — игры, лидерборды, комментарии, скины
 *
 * Авторизация — сырой JWT в заголовке Authorization (см. client.js).
 */

import { request, API_BASE, getToken } from "./client";
import * as v2Api from "./v2";
// Мост старых числовых ссылок на профиль → UUID аккаунта v2.
import { resolveAccountId, resolveV1Id } from "./identityV2";
// Список ключей тем — из того же модуля, где палитры,
// чтобы каталог и оформление не разъезжались.
import { CAB_THEMES as CAB_THEME_KEYS } from "../theme/cabThemes";

const U = "/api-user/api/v1/users";
const S = "/api-storys/kubikon3d";

/* ── профиль ─────────────────────────────────────────────────────── */

/**
 * Профиль текущего пользователя.
 *
 * Сначала identity v2, при неудаче — v1. Тот же приём, что уже применён
 * к косметике: пока обе версии живы, сайт работает с любой; когда v1
 * отключат, запасной путь просто перестанет срабатывать, и трогать код
 * повторно не придётся.
 *
 * Ответ v2 приводим к форме v1 (firstName/photo/level): её ждут кабинет
 * и десяток экранов, а к переезду они отношения не имеют.
 */
export async function getMyProfile(signal) {
  try {
    const me = await v2Api.getMe(signal);
    if (me && me.id) {
      return {
        ...me,
        firstName: me.nickname || me.username || null,
        photo: me.avatar_url || null,
        level: me.level ?? 0,
        active_frame: me.frame || null,
        name_color: me.name_color || null,
        // account_id пригодится для ссылок на профиль: в v2 они UUID-ные.
        account_id: me.id,
      };
    }
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    // 401 пробрасываем: это «нужно войти», а не «попробуй старым способом».
    if (e?.status === 401) throw e;
  }
  const res = await request(`${U}/profile`, { auth: true, signal });
  return (res && res.data) || {};
}

/**
 * Обновить профиль (multipart). Все поля опциональны; передавай только то,
 * что меняешь. photo — File или null/undefined.
 * Возвращает {ok, message, data?}.
 */
export async function putMyProfile(fields, signal) {
  const formData = new FormData();
  // photo (опциональный)
  if (fields.photo instanceof File) formData.append("photo", fields.photo);
  // ВСЕГДА обязательны (бэкенд читает их через request.form.getlist('x')[0]:
  // если поля нет — IndexError → 500). Пустая строка норм.
  const required = [
    "firstName",
    "lastName",
    "city",
    "country",
    "status",
    "lang",
    "basicCourse",
  ];
  for (const k of required) {
    const v = fields[k];
    formData.append(k, v == null ? "" : String(v));
  }
  // grade — опциональный (бэк проверяет через if request.form.getlist('grade'))
  if (fields.grade !== undefined && fields.grade !== "") {
    formData.append("grade", String(fields.grade));
  }
  const token = getToken();
  const res = await fetch(`${API_BASE}/api-user/api/v1/users/profile`, {
    method: "PUT",
    headers: token ? { Authorization: token } : {},
    body: formData,
    signal,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    const msg = (data && (data.message || data.error)) || `Ошибка ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data || {};
}

/**
 * Загрузить обложку профиля (платно, 20 алмазов — цена зашита на бэке).
 * file — File (jpeg/png/gif). Возвращает {message, coverPhoto, diamonds}.
 */
export async function uploadProfileCover(file) {
  const formData = new FormData();
  formData.append("photo", file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/api-user/api/v1/users/profile/cover`, {
    method: "POST",
    headers: token ? { Authorization: token } : {},
    body: formData,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    const msg = (data && (data.message || data.error)) || `Ошибка ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data || {};
}

/** Убрать обложку профиля (бесплатно, алмазы не возвращаются). */
export async function deleteProfileCover() {
  return request(`${U}/profile/cover`, { method: "DELETE", auth: true });
}

/**
 * Загрузить фон страницы профиля (платно, 80 алмазов — цена зашита на бэке).
 * file — File (jpeg/png/gif). Возвращает {message, backgroundPhoto, diamonds}.
 */
export async function uploadProfileBackground(file) {
  const formData = new FormData();
  formData.append("photo", file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/api-user/api/v1/users/profile/background`, {
    method: "POST",
    headers: token ? { Authorization: token } : {},
    body: formData,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    const msg = (data && (data.message || data.error)) || `Ошибка ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data || {};
}

/** Убрать фон страницы профиля (бесплатно, алмазы не возвращаются). */
export async function deleteProfileBackground() {
  return request(`${U}/profile/background`, { method: "DELETE", auth: true });
}

/**
 * Настройки отображения фона (режим/позиция/масштаб/плитка/затемнение).
 * Бесплатно. settings: {mode, posX, posY, zoom, tile, dim} — бэк валидирует
 * диапазоны. Возвращает {message, background_settings}.
 */
export async function putBackgroundSettings(settings) {
  return request(`${U}/profile/background/settings`, {
    method: "PUT", auth: true, body: settings,
  });
}

/** Те же настройки отображения для обложки. Возвращает {message, cover_settings}. */
export async function putCoverSettings(settings) {
  return request(`${U}/profile/cover/settings`, {
    method: "PUT", auth: true, body: settings,
  });
}

/** «О себе» — описание профиля до 600 символов чистого текста (пусто =
 * убрать). rich — JSON-структура оформления (см. AboutEditor.jsx) или
 * null для плоского текста. Возвращает {message, about, about_rich}. */
export async function putProfileAbout(about, rich = null) {
  return request(`${U}/profile/about`, {
    method: "PUT", auth: true, body: { about, rich },
  });
}

/* ── косметика: рамки аватара + цвет имени ───────────────────────── */

/**
 * Приводит каталог косметики v2 к форме v1.
 *
 * v2 отдаёт {owned:[id], active:id, catalog:[{id,name,...}]}, а весь код
 * сайта ждёт плоский список, где owned/active лежат в самом элементе.
 * Переименование держим в одном месте — иначе пришлось бы править
 * все экраны косметики.
 */
function adaptV2Catalog(d) {
  const owned = new Set(d?.owned || []);
  const active = d?.active;
  return (d?.catalog || []).map((c) => ({
    ...c,
    key: c.id,
    title: c.name,
    owned: owned.has(c.id),
    active: c.id === active,
  }));
}

/** Каталог рамок с owned/active — [{key,title,price,style,currency,owned,active}]. */
export async function getFrames(signal) {
  // v2 — источник правды по косметике: набор рамок там тот же (портирован
  // из Маськи 1:1), а покупка списывает алмазы из общего кошелька.
  try {
    const d = await v2Api.getFrames(signal);
    if (d?.catalog) return adaptV2Catalog(d);
  } catch (e) {
    if (e?.name === "AbortError" || e?.status === 401) throw e;
  }
  const d = await request(`${U}/cosmetics/frames`, { auth: true, signal });
  return Array.isArray(d?.frames) ? d.frames : [];
}

/** Купить рамку. Возвращает {ok, diamonds?|stars?} или {ok:false, error}. */
export async function buyFrame(key) {
  try {
    const r = await v2Api.buyFrame(key);
    return { ok: true, ...r };
  } catch (e) {
    if (e?.status === 401) throw e;
    if (e?.status === 400 || e?.status === 403) return { ok: false, error: e.message };
  }
  return request(`${U}/cosmetics/frames/buy`, {
    method: "POST", auth: true, body: { key },
  });
}

/** Надеть рамку (key "none" — снять). */
export async function activateFrame(key) {
  try {
    await v2Api.activateFrame(key);
    return { ok: true };
  } catch (e) {
    if (e?.status === 401) throw e;
  }
  return request(`${U}/cosmetics/frames/activate`, {
    method: "POST", auth: true, body: { key },
  });
}

/** Каталог цветов имени — [{key,title,color,price,currency,owned,active}]. */
export async function getNameColors(signal) {
  try {
    const d = await v2Api.getNameColors(signal);
    if (d?.catalog) return adaptV2Catalog(d);
  } catch (e) {
    if (e?.name === "AbortError" || e?.status === 401) throw e;
  }
  const d = await request(`${U}/cosmetics/name-colors`, { auth: true, signal });
  return Array.isArray(d?.colors) ? d.colors : [];
}

export async function buyNameColor(key) {
  return request(`${U}/cosmetics/name-colors/buy`, {
    method: "POST", auth: true, body: { key },
  });
}

/** Активировать цвет имени (key "default" — снять). */
export async function activateNameColor(key) {
  return request(`${U}/cosmetics/name-colors/activate`, {
    method: "POST", auth: true, body: { key },
  });
}

/** Баланс звёзд (донатная валюта). */
export async function getMyStars(signal) {
  try {
    const b = await balancesOnce(signal);
    if (b && typeof b.star === "number") return b.star;
  } catch (e) {
    if (e?.name === "AbortError") throw e;
  }
  const d = await request(`${U}/star`, { auth: true, signal });
  return d && typeof d.count === "number" ? d.count : 0;
}

/* ── темы кабинета ───────────────────────────────────────────────── */

/**
 * Сопоставление тем Рублокса и школы.
 *
 * Темы у нас и у школы — РАЗНЫЕ по природе. В Рублоксе тема это палитра
 * CSS-переменных (ocean — синяя, sakura — розовая). В школе это целый
 * React-layout со своей вёрсткой (Киберпанк, Пиксель, Терминал — разные
 * интерфейсы, а не цвета), 3300 строк, завязанных на её разметку.
 *
 * Поэтому каталог школы мы НЕ показываем: купив «Терминал», человек не
 * получил бы терминальный вид — в Рублоксе другая разметка.
 *
 * Но владение и ОПЛАТА идут через v2 — это и даёт единый кошелёк, ради
 * которого затевался переезд. Каждой теме Рублокса сопоставлена тема
 * школы близкой цены: покупка списывает алмазы из общего кошелька v2,
 * а показываем мы свою палитру.
 *
 * classic/dark бесплатны — им сопоставлять нечего.
 */
const THEME_V2_MAP = {
  ocean:  "blueprint",   // 150 — синий «Чертёж»
  rublox: "duotone",     // 150 — фирменный двухцветный
  sakura: "memphis",     // 300 — розовый «Мемфис»
  forest: "bauhaus",     // 300
  sunset: "retrofut",    // 500 — тёплый «Ретрофутуризм»
};
const THEME_V2_BACK = Object.fromEntries(
  Object.entries(THEME_V2_MAP).map(([ours, theirs]) => [theirs, ours])
);

/** Каталог тем кабинета — [{key,price,owned,active}]. Палитры в theme/cabThemes.js. */
export async function getAppThemes(signal) {
  // Владение берём из v2 (единый кошелёк), а список и палитры — свои.
  try {
    const d = await v2Api.getThemes(signal);
    if (d?.catalog) {
      const owned = new Set(d.owned || []);
      const priceOf = Object.fromEntries(
        (d.catalog || []).map((c) => [c.id, c.price])
      );
      return Object.keys(CAB_THEME_KEYS).map((key) => {
        const mapped = THEME_V2_MAP[key];
        return {
          key,
          price: mapped ? priceOf[mapped] ?? 0 : 0,
          // Бесплатные принадлежат всем; платные — если куплена
          // соответствующая тема школы.
          owned: !mapped || owned.has(mapped),
          active: false, // активную ставит CabinetContext из своего хранилища
        };
      });
    }
  } catch (e) {
    if (e?.name === "AbortError" || e?.status === 401) throw e;
  }
  const d = await request(`${U}/cosmetics/themes`, { auth: true, signal });
  return Array.isArray(d?.themes) ? d.themes : [];
}

/** Купить тему (алмазы). Возвращает {ok, diamonds?} или {ok:false, error}. */
export async function buyAppTheme(key) {
  const mapped = THEME_V2_MAP[key];
  if (mapped) {
    try {
      const r = await v2Api.buyTheme(mapped);
      return { ok: true, ...r };
    } catch (e) {
      if (e?.status === 401) throw e;
      if (e?.status === 400 || e?.status === 403) {
        return { ok: false, error: e.message };
      }
    }
  }
  return request(`${U}/cosmetics/themes/buy`, {
    method: "POST", auth: true, body: { key },
  });
}

/** Включить тему (key "classic" — дефолт). */
export async function activateAppTheme(key) {
  const mapped = THEME_V2_MAP[key];
  if (mapped) {
    try {
      await v2Api.activateTheme(mapped);
      return { ok: true };
    } catch (e) {
      if (e?.status === 401) throw e;
    }
  }
  return request(`${U}/cosmetics/themes/activate`, {
    method: "POST", auth: true, body: { key },
  });
}

/** Ключ темы школы по нашему ключу — для отладки и обратного сопоставления. */
export function themeKeyToV2(key) {
  return THEME_V2_MAP[key] || null;
}
export function themeKeyFromV2(v2key) {
  return THEME_V2_BACK[v2key] || null;
}

/* ── звёзды: конвертация ─────────────────────────────────────────── */

/**
 * Конвертация звёзд в алмазы (5★ = 1💎, необратимо). stars кратно 5.
 * Возвращает {ok, converted_stars, diamonds_added, stars_remaining, diamonds_total}.
 */
export async function convertStars(stars) {
  return request(`${U}/star/convert`, {
    method: "POST", auth: true, body: { stars },
  });
}

/* ── достижения Рублокса ─────────────────────────────────────────── */

/**
 * Свои достижения Рублокса (НЕ минкрафтийские). Прогресс уже посчитан бэком:
 * {achievs: [{key,title,description,icon,count,value,received}],
 *  received_count, total}. icon — имя CabIcon.
 */
export async function getRubloxAchievs(userId, signal) {
  const d = await request(`${U}/rublox-achievs/${userId}`, { auth: true, signal });
  return {
    achievs: Array.isArray(d?.achievs) ? d.achievs : [],
    receivedCount: d?.received_count || 0,
    total: d?.total || 0,
  };
}

/* ── медали Рублокса ─────────────────────────────────────────────── */

/**
 * Медали Рублокса — {medals:[{key,title,description,tier,event,count,
 * value,received,earned_at,meta}], receivedCount, total}. Заработанная
 * медаль зафиксирована бэком навсегда (таблица RubloxMedal).
 */
export async function getRubloxMedals(userId, signal) {
  const d = await request(`${U}/rublox-medals/${userId}`, { auth: true, signal });
  return {
    medals: Array.isArray(d?.medals) ? d.medals : [],
    receivedCount: d?.received_count || 0,
    total: d?.total || 0,
  };
}

/**
 * Значения стат-виджетов шапки — {plays,games,likes,friends,gifts,days,
 * achievs,medals}. Какие из них показывать — выбирает владелец
 * (profile_layout.widgets).
 */
export async function getRubloxStats(userId, signal) {
  const d = await request(`${U}/rublox-stats/${userId}`, { auth: true, signal });
  return d?.stats && typeof d.stats === "object" ? d.stats : null;
}

/* ── бейджи у имени ──────────────────────────────────────────────── */

/**
 * Каталог бейджей ТЕКУЩЕГО юзера — {badges:[{key,title,description,icon,
 * earned,displayed}], displayed:[...], max}. Побочный эффект на бэке:
 * обновляется кэш заработанных (owned_badges).
 */
export async function getBadges(signal) {
  const d = await request(`${U}/cosmetics/badges`, { auth: true, signal });
  return {
    badges: Array.isArray(d?.badges) ? d.badges : [],
    displayed: Array.isArray(d?.displayed) ? d.displayed : [],
    max: d?.max || 3,
  };
}

/**
 * Выбрать до 3 бейджей у имени (пустой массив = убрать все).
 * Возвращает {ok, displayed_badges} — CSV для profile.
 */
export async function putDisplayedBadges(keys) {
  return request(`${U}/cosmetics/badges`, {
    method: "PUT", auth: true, body: { keys },
  });
}

/* ── оформление профиля (layout) ─────────────────────────────────── */

/**
 * Сохранить оформление профиля: {theme, order, hidden, panel, titleFont,
 * titles, widgets}. Возвращает {ok, profile_layout} — сохранённую JSON-строку.
 */
export async function putProfileLayout(layout) {
  return request(`${U}/profile/layout`, {
    method: "PUT", auth: true, body: layout,
  });
}

/* ── подарки ─────────────────────────────────────────────────────── */

/** Каталог подарков — [{key,title,price,anim,lottie}]. Цены в алмазах. */
export async function getGiftsCatalog(signal) {
  const d = await request(`${U}/gifts/catalog`, { auth: true, signal });
  return Array.isArray(d?.gifts) ? d.gifts : [];
}

/** Подарить. Возвращает {ok, gift, diamonds} или {ok:false, error, message}. */
export async function sendGift(toUserId, key, message = "", anonymous = false) {
  return request(`${U}/gifts/send`, {
    method: "POST", auth: true,
    body: { to_user_id: toUserId, key, message, anonymous },
  });
}

/** Полка подарков пользователя — {gifts:[...], count}. */
export async function getGiftShelf(userId, signal) {
  const d = await request(`${U}/gifts/user/${userId}`, { auth: true, signal });
  return {
    gifts: Array.isArray(d?.gifts) ? d.gifts : [],
    count: d?.count || 0,
  };
}

/* ── экономика / рейтинг ─────────────────────────────────────────── */

/* Кабинет запрашивает алмазы, звёзды и рейтинг одновременно (Promise.all
   в refreshEconomy), а кошелёк v2 отдаёт их ОДНИМ ответом. Без склейки
   ушло бы три одинаковых запроса вместо одного.
   Окно 1.5 с: три вызова из одного тика попадут в него гарантированно,
   а данные не успеют устареть. */
let _balCache = null;
let _balAt = 0;
async function balancesOnce(signal) {
  const now = Date.now();
  if (_balCache && now - _balAt < 1500) return _balCache;
  const b = await v2Api.getBalances(signal);
  _balCache = b;
  _balAt = now;
  return b;
}

/* Экономика: кошелёк v2 отдаёт алмазы, звёзды и рейтинг ОДНИМ запросом
   (`/wallet/balances`). Эти три функции ходили только в v1 напрямую,
   минуя v2 — после переезда токен v1 не принимает, и в кабинете были
   прочерки вместо балансов.
   v1 остаётся запасным путём, пока жив. */
export async function getMyDiamonds(signal) {
  try {
    const b = await balancesOnce(signal);
    if (b && typeof b.diamond === "number") return b.diamond;
  } catch (e) {
    if (e?.name === "AbortError") throw e;
  }
  const d = await request(`${U}/diamond`, { auth: true, signal });
  return d && typeof d.count === "number" ? d.count : 0;
}

export async function getMyRating(signal) {
  try {
    const b = await balancesOnce(signal);
    if (b && typeof b.rating === "number") return b.rating;
  } catch (e) {
    if (e?.name === "AbortError") throw e;
  }
  const d = await request(`${U}/rating/me`, { auth: true, signal });
  return d && typeof d.rating === "number" ? d.rating : 0;
}

/** Топ игроков. metric: "rating" | "diamond". */
export async function getTopPlayers({ metric = "rating", limit = 100 } = {}, signal) {
  const d = await request(
    `${U}/top?metric=${encodeURIComponent(metric)}&limit=${limit}`,
    { signal }
  );
  return Array.isArray(d?.data) ? d.data : [];
}

/** Текущий месяц битвы рейтингов — {data,total,my_place,my_rating}. */
export async function getBattleRatingCurrent(signal) {
  const d = await request(`${U}/battle-rating/current`, { auth: true, signal });
  return {
    data: Array.isArray(d?.data) ? d.data : [],
    total: d?.total || 0,
    myPlace: d?.my_place || 0,
    myRating: d?.my_rating || 0,
  };
}

/** Архив битвы рейтингов (победители прошлых месяцев). */
export async function getBattleRatingArchive(limit = 12, signal) {
  const d = await request(`${U}/battle-rating/archive?limit=${limit}`, {
    auth: true,
    signal,
  });
  return Array.isArray(d?.data) ? d.data : [];
}

/* ── друзья / запросы ────────────────────────────────────────────── */

export async function getFriends(signal) {
  const d = await request(`${U}/friends`, { auth: true, signal });
  return Array.isArray(d?.friends) ? d.friends : [];
}

/** Запросы в друзья — {incoming:[...], outgoing:[...]}. */
export async function getFriendRequests(signal) {
  const d = await request(`${U}/friends/requests`, { auth: true, signal });
  return {
    incoming: Array.isArray(d?.incoming) ? d.incoming : [],
    outgoing: Array.isArray(d?.outgoing) ? d.outgoing : [],
  };
}

export async function sendFriendRequest(toUserId) {
  await request(`${U}/friends/request`, {
    method: "POST",
    auth: true,
    body: { to_user_id: toUserId },
  });
}

export async function acceptFriendRequest(requestId) {
  await request(`${U}/friends/accept`, {
    method: "POST",
    auth: true,
    body: { request_id: requestId },
  });
}

/** Отклонить входящий ИЛИ отозвать исходящий запрос — один эндпоинт. */
export async function rejectFriendRequest(requestId) {
  await request(`${U}/friends/reject`, {
    method: "POST",
    auth: true,
    body: { request_id: requestId },
  });
}

/**
 * Получить публичный профиль пользователя по id.
 * Возвращает данные из Profile-таблицы (имя, фото, дата регистрации и т.п.).
 * Требует auth (любой залогиненный юзер может смотреть чужие профили).
 */
export async function getUserById(userId, signal) {
  /* Единственная точка загрузки чужого профиля — поэтому совместимость
     ссылок решается здесь, а не в десяти местах, где строится URL.

     Ссылки по всему Рублоксу выглядят как /app/profile/1 (числовой v1-id),
     а identity v2 принимает ТОЛЬКО UUID (проверено: по числу отвечает
     «Пользователь не найден»). Числовой id сводим к UUID через мост
     на нашем бэкенде — у него есть доступ к internal-эндпоинтам. */
  try {
    const accountId = await resolveAccountId(userId, signal);
    if (accountId) {
      const u = await v2Api.getUser(accountId, signal);
      if (u && u.id) {
        // Приводим к форме v1: её ждут ProfileHeader и секции страницы.
        return {
          ...u,
          user_id: u.id,
          firstName: u.nickname || u.username || null,
          photo: u.avatar_url || null,
          level: u.level ?? 0,
          active_frame: u.frame || null,
          name_color: u.name_color || null,
        };
      }
    }
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    // Прочие ошибки не валят страницу: пробуем v1, пока он жив.
  }

  const d = await request(`${U}/profile/${userId}`, { auth: true, signal });
  // Бэк возвращает { data: {...} } при успехе, { message: "Пользователь не найден" } при 404 в логике (тоже HTTP 200).
  if (d?.data && typeof d.data === "object") return d.data;
  if (d?.message && /не найден/i.test(d.message)) return null;
  return null;
}

/** Доп. данные профиля (рейтинг, алмазы, idRecords ачивок). */
export async function getUserExtras(userId, signal) {
  return request(`${U}/profileData/${userId}`, { auth: true, signal });
}

/**
 * Полные данные Rating пользователя (для прогресса достижений):
 * {idRecords, countAddTask, addLevelTask, addTaskDay, addTaskWeek, dalikCount,...}
 * idRecords — JSON-строка {"add":[...полученные id], "process":[...в процессе]}.
 */
export async function getRatingForId(userId, signal) {
  const d = await request(`${U}/rating/${userId}`, { auth: true, signal });
  return d?.data || null;
}

/** История лидеров (для медалей за призовые места). */
export async function getLeadersHistory(signal) {
  const d = await request(`${U}/rating/leaders/history`, { auth: true, signal });
  return d && typeof d === "object" ? d : {};
}

/** Лидеры по курсам (для медалей «лидер курса»). */
export async function getAllCourseLeaders(signal) {
  const d = await request(`${U}/learning/all-course-leaders`, { auth: true, signal });
  return d && typeof d === "object" ? d : {};
}

/** Группа достижений по id-строке "1,5,12".
 * Бэк achievs отдаёт {data:[{id,name,description,icon,howReyting,count}]}. */
export async function getAchievsGroup(ids, signal) {
  if (!ids) return [];
  const d = await request(
    `/api-achievs/api/v1/achievs/${encodeURIComponent(ids)}`,
    { auth: true, signal },
  );
  return Array.isArray(d?.data) ? d.data
       : Array.isArray(d) ? d
       : (d?.achievs || []);
}

/** Все ачивки (для секции «всего достижений»). */
export async function getAllAchievs(signal) {
  const d = await request(`/api-achievs/api/v1/achievs/all`, { auth: true, signal });
  return Array.isArray(d?.data) ? d.data
       : Array.isArray(d) ? d
       : (d?.achievs || []);
}

/* ── комментарии на профиле пользователя ──────────────────────────── */

const C = "/api-comments/api/v1/comments";

/**
 * Получить комментарии профиля. Бэк возвращает {data: [{id, textComent, idUser, timeComent, idRasdel}]}.
 * Нормализуем поля под фронт + bulk-резолвим имена и аватары авторов.
 * Комментарии общие с Майнкрафтией — таблица commentsProfileUser в comments_db
 * шарится между MН и Рублоксом, юзеры видят одни и те же записи.
 */
export async function getProfileComments(userId, signal) {
  const d = await request(`${C}/commentsProfileUser/${userId}`, { auth: true, signal });
  const raw = Array.isArray(d?.data) ? d.data
            : Array.isArray(d?.comments) ? d.comments
            : Array.isArray(d) ? d : [];

  // Нормализуем поля бэк → фронт-формат
  const comments = raw.map((c) => ({
    id: c.id,
    text: c.textComent || c.text || "",
    userId: c.idUser != null ? Number(c.idUser) : null,
    timeComent: c.timeComent || c.timestamp || "",
  }));

  // Сортировка: новые сверху. timeComent — unix-timestamp в виде строки
  // (см. comments_generic.py:129 → str(time.time())). Если поле пустое
  // или нечисловое — кладём такие записи в конец.
  comments.sort((a, b) => {
    const ta = parseFloat(a.timeComent) || 0;
    const tb = parseFloat(b.timeComent) || 0;
    return tb - ta;
  });

  // Bulk-резолв авторов (имена + аватары). Один запрос на всех.
  const authorIds = Array.from(new Set(
    comments.map((c) => c.userId).filter((id) => id != null)
  ));
  if (authorIds.length === 0) return comments;
  try {
    const r = await request(`${U}/profiles/bulk`, {
      method: "POST",
      body: { user_ids: authorIds },
      signal,
    });
    const profiles = Array.isArray(r?.profiles) ? r.profiles : [];
    const byId = new Map(profiles.map((p) => [Number(p.id), p]));
    for (const c of comments) {
      const p = byId.get(c.userId);
      if (p) {
        c.firstName = p.firstName || "Пользователь";
        c.photo = p.photo || "";
        c.photo_thumb_b64 = p.photo_thumb_b64 || "";
      }
    }
  } catch {
    // bulk недоступен — комментарии остаются без имён (покажем "Пользователь")
  }
  return comments;
}

export async function postProfileComment(toUserId, text) {
  return request(`${C}/commentsProfileUser`, {
    method: "POST", auth: true,
    body: { idRasdel: toUserId, textComent: text },
  });
}

export async function deleteMyProfileComment(commentId) {
  return request(`${C}/commentsProfileUser/${commentId}`, {
    method: "DELETE", auth: true,
  });
}

export async function getProfileJudgement(userId, signal) {
  const d = await request(`${C}/judgement/${userId}/ProfileUser`, { auth: true, signal });
  return d && typeof d === "object" ? d : {};
}

export async function putProfileJudgement(userId, change) {
  // change: '+' | '-'
  return request(`${C}/judgement`, {
    method: "PUT", auth: true,
    body: { idRasdel: userId, change, rasdel: "ProfileUser" },
  });
}

/* ── мост идентификаторов для storys ──────────────────────────────
   storys живёт на числовых id: его маршруты объявлены как
   `<int:user_id>`, а параметры разбираются через `type=int`. После
   переезда на identity v2 фронт держит UUID — если отдать его как есть,
   получаем 400 («user_id required», потому что int() вернул None) или
   даже 500 там, где стоит голый int() без защиты (projects/by-author).

   Поэтому ЛЮБОЙ вызов storys с идентификатором человека обязан пройти
   через storysUserId(). Пока storys не переведён на UUID, это
   единственное место, где хранится знание о различии версий.

   Объявлено здесь, до первого использования: ниже по файлу лежат ещё
   несколько вызовов, и подъём объявления функции не должен быть
   единственным, что удерживает их от поломки. */
async function storysUserId(userId) {
  const value = String(userId || "");
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(value)) return value; // уже число
  const v1 = await resolveV1Id(value);
  return v1 || value;
}

/** История последних игр конкретного пользователя.
    auth обязателен: с 30.06.2026 история — приватные данные, сервер
    сверяет владельца по токену и без заголовка отвечает 401. */
export async function getUserPlayHistory(userId, limit = 5, signal) {
  const uid = await storysUserId(userId);
  const d = await request(
    `${S}/users/${uid}/history?limit=${limit}`,
    { auth: true, signal },
  );
  return Array.isArray(d?.projects) ? d.projects : [];
}

/** Получить игры конкретного автора (для страницы публичного профиля). */
export async function getProjectsByAuthor(userId, limit = 24, signal) {
  const uid = await storysUserId(userId);
  const d = await request(
    `${S}/projects/by-author?user_id=${uid}&limit=${limit}`,
    { signal },
  );
  return Array.isArray(d?.projects) ? d.projects : [];
}

export async function searchUsers(query, signal) {
  const d = await request(`${U}/friends/search?q=${encodeURIComponent(query)}`, {
    auth: true,
    signal,
  });
  return Array.isArray(d?.users) ? d.users : [];
}

/* Личные чаты (getConversations/getDmHistory/sendDm) УДАЛЕНЫ 2026-07-09:
 * переписка переехала в Маську (кнопка «Написать» у друга → ?dm=<id>).
 * Бэк-роуты /messages пока живы — старые переписки лежат в БД. */

/* ── игры (Storys) ───────────────────────────────────────────────── */

/**
 * Лента игр (умная лента Рублокса).
 * tab: recommended|new|popular|top_week.
 *   recommended — умная лента по hot_score (лайки/время/запуски/свежесть).
 * Параметр sort оставлен для обратной совместимости (= алиас tab).
 */
export async function getFeed(
  {
    page = 1,
    perPage = 24,
    tab = "recommended",
    sort,
    maxAge = 18,
    genre = "",
  } = {},
  signal
) {
  const q = new URLSearchParams({
    page,
    per_page: perPage,
    // tab — основной параметр умной ленты; sort принимаем как алиас.
    tab: tab || sort || "recommended",
    rank: "all",
    max_age: maxAge,
  });
  if (genre) q.set("genre", genre);
  const d = await request(`${S}/feed?${q.toString()}`, { signal });
  return Array.isArray(d?.projects) ? d.projects : [];
}

/**
 * То же что getFeed, но возвращает полный объект страницы с метаданными
 * пагинации: { projects, total, page }. Нужно для бесконечной прокрутки
 * (знать, есть ли ещё игры). Старый getFeed оставлен для совместимости.
 */
export async function getFeedPage(
  {
    page = 1,
    perPage = 24,
    tab = "recommended",
    sort,
    maxAge = 18,
    genre = "",
  } = {},
  signal
) {
  const q = new URLSearchParams({
    page,
    per_page: perPage,
    tab: tab || sort || "recommended",
    rank: "all",
    max_age: maxAge,
  });
  if (genre) q.set("genre", genre);
  const d = await request(`${S}/feed?${q.toString()}`, { signal });
  const projects = Array.isArray(d?.projects) ? d.projects : [];
  const total = typeof d?.total === "number" ? d.total : projects.length;
  return { projects, total, page: d?.page || page };
}

export async function searchProjects(query, maxAge = 18, signal) {
  const d = await request(
    `${S}/search?q=${encodeURIComponent(query)}&max_age=${maxAge}`,
    { signal }
  );
  return Array.isArray(d?.projects) ? d.projects : [];
}

/**
 * Загрузить игру.
 *
 * userId обязателен для СВОИХ черновиков: опубликованное storys отдаёт
 * всем, а draft/review/blocked — только владельцу, и владельца он
 * определяет по `?user_id=`. Без него автор получал 403 на собственной
 * игре («Не удалось загрузить: forbidden»).
 *
 * auth тоже нужен: по токену storys пропускает модератора команды
 * к игре на проверке.
 */
export async function getProject(id, userId, signal) {
  const q = userId ? `?user_id=${encodeURIComponent(await storysUserId(userId))}` : "";
  return request(`${S}/projects/${id}${q}`, { auth: true, signal });
}

export async function getTrending(limit = 12, signal) {
  const d = await request(`${S}/trending?limit=${limit}`, { signal });
  return Array.isArray(d?.projects) ? d.projects : d || [];
}

export async function getTopAuthors(limit = 100, signal) {
  const d = await request(`${S}/top-authors?limit=${limit}`, { signal });
  return Array.isArray(d?.authors) ? d.authors : [];
}

export async function getActivity(limit = 10, signal) {
  const d = await request(`${S}/activity?limit=${limit}`, { signal });
  return Array.isArray(d?.activity) ? d.activity : [];
}

/* storys живёт на ЧИСЛОВЫХ id: маршруты объявлены как <int:user_id>,
   и вся его база (проекты, лайки, сейвы, лидерборды) привязана к числу.
   Сайт после переезда на identity v2 оперирует UUID, поэтому перед
   обращением сводим id к числовому через мост нашего бэкенда.
   Без этого запросы уходили как /users/<uuid>/history и давали 404.

   Полный переход storys на UUID — отдельная работа (миграция схемы
   и данных), к переключению авторизации она не относится. */
export async function getPlayHistory(userId, limit = 12, signal) {
  const uid = await storysUserId(userId);
  const d = await request(`${S}/users/${uid}/history?limit=${limit}`, {
    auth: true,
    signal,
  });
  return Array.isArray(d?.projects) ? d.projects : d || [];
}

export async function getFavorites(userId, signal) {
  const uid = await storysUserId(userId);
  const d = await request(`${S}/users/${uid}/favorites`, {
    auth: true,
    signal,
  });
  return Array.isArray(d?.projects) ? d.projects : d || [];
}

/* ── страница игры: лайки / комментарии / лидерборд ──────────────── */

export async function getLeaderboard(projectId, limit = 20, signal) {
  const d = await request(
    `${S}/projects/${projectId}/leaderboard?limit=${limit}`,
    { signal }
  );
  return Array.isArray(d?.records) ? d.records : [];
}

/** Статус лайка пользователя — {liked, vote}. */
export async function getLikeStatus(projectId, userId, signal) {
  const uid = await storysUserId(userId);
  return request(`${S}/projects/${projectId}/like-status?user_id=${uid}`, {
    signal,
  });
}

/** Переключить лайк. kind: like|dislike|none. */
export async function toggleLike(projectId, userId, kind) {
  const uid = await storysUserId(userId);
  return request(`${S}/projects/${projectId}/like`, {
    method: "POST",
    auth: true,
    body: { user_id: uid, kind },
  });
}

export async function getComments(projectId, signal) {
  const d = await request(`${S}/projects/${projectId}/comments`, { signal });
  const list = Array.isArray(d?.comments) ? d.comments : [];
  return list.filter((c) => !c.hidden);
}

export async function postComment(projectId, userId, text) {
  const uid = await storysUserId(userId);
  await request(`${S}/projects/${projectId}/comments`, {
    method: "POST",
    auth: true,
    body: { user_id: uid, text },
  });
}

/* ── скины ───────────────────────────────────────────────────────── */

export async function getOwnedSkins(userId, signal) {
  const uid = await storysUserId(userId);
  const d = await request(`${S}/rublox/owned-skins?user_id=${uid}`, {
    signal,
  });
  return Array.isArray(d?.skins) ? d.skins : [];
}

export async function purchaseSkin(userId, skinFolder, price = 100, promoCode = "") {
  /* Через мост обязательно: это ЗАПИСЬ владения. Уйдёт UUID — покупка
     ляжет на несуществующего владельца, и человек не увидит купленное
     ни в кабинете, ни в плеере, хотя алмазы спишутся. */
  const uid = await storysUserId(userId);
  const d = await request(`${S}/rublox/purchase-skin`, {
    method: "POST",
    auth: true,
    body: {
      user_id: uid,
      skin_folder: skinFolder,
      price,
      ...(promoCode ? { promo_code: promoCode } : {}),
    },
  });
  // возвращаем весь ответ: { remaining_diamonds, promo_applied, price_paid, ... }
  return d || null;
}

/* «Надеть» скин — сохраняет выбор в rublox_equipped_skin (для серверной
 * валидации и отображения на других клиентах). Вызывает /equipped-skin POST.
 * user_id берётся из JWT-токена. */
export async function setEquippedSkin(skinFolder) {
  await request(`${S}/rublox/equipped-skin`, {
    method: "POST",
    auth: true,
    body: { skin_folder: skinFolder },
  });
}

/* Прочитать надетый скин из БД (rublox_equipped_skin). Нужно чтобы кабинет
 * /app/avatar показывал тот же скин что в плеере (между устройствами).
 * Возвращает skin_folder (строка) или null. user_id из JWT. */
export async function getEquippedSkin(userId) {
  try {
    const uid = await storysUserId(userId);
    const res = await request(
      `${S}/rublox/equipped-skin?user_id=${uid}`,
      { method: "GET", auth: true });
    return res?.skin_folder || null;
  } catch {
    return null;
  }
}

/* ── баг-репорты ─────────────────────────────────────────────────── */

export async function postBugReport({
  bugType,
  title,
  message,
  urlPath,
  userId,
  projectId,
}) {
  /* Тоже через мост: иначе в жалобах окажется UUID, который потом
     не сопоставить с автором в модераторской панели (она на числах). */
  const uid = userId ? await storysUserId(userId) : null;
  await request(`${S}/bug-reports`, {
    method: "POST",
    auth: true,
    body: {
      bug_type: bugType,
      title,
      message,
      url_path: urlPath || "",
      user_agent: navigator.userAgent,
      user_id: uid || null,
      project_id: projectId || null,
    },
  });
}

/* ── вспомогательное ─────────────────────────────────────────────── */

/**
 * Аватар base64 → data-URI. Бэкенд отдаёт photo_thumb_b64 как «голый» base64
 * PNG 96×96 без префикса. Если уже data-URI или пусто — возвращаем как есть.
 */
export function avatarSrc(b64) {
  if (!b64) return null;
  if (b64.startsWith("data:")) return b64;
  return `data:image/png;base64,${b64}`;
}

/**
 * Полный URL фото профиля. Бэк возвращает только filename
 * (например "10.png") — собираем абсолютный URL.
 * Если уже полный http(s) URL или data: — возвращаем как есть.
 */
export function profilePhotoSrc(photo) {
  if (!photo) return null;
  if (photo.startsWith("http://") || photo.startsWith("https://")) return photo;
  if (photo.startsWith("data:")) return photo;
  return `${API_BASE}/api-user/assets/profile/${photo}`;
}

/* ── мои игры (раздел «Создавать») ───────────────────────────────── */

/**
 * Игры автора — ВСЕ, включая черновики и отклонённые.
 *
 * До сих пор этих данных в интерфейсе не было вовсе, хотя они есть:
 * у активного автора счёт идёт на сотни черновиков против десятков
 * опубликованных. Управлять ими было негде.
 *
 * Возвращает массив с полями status/quality_state/moderator_comment —
 * по ним видно, что с игрой: черновик, на проверке, отклонена.
 */
export async function getMyProjects(userId, signal) {
  const uid = await storysUserId(userId);
  const d = await request(`${S}/my-projects?user_id=${encodeURIComponent(uid)}`, {
    auth: true,
    signal,
  });
  if (Array.isArray(d)) return d;
  return Array.isArray(d?.projects) ? d.projects : [];
}
